import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useHistoryColumns } from '@r/components/history/history-columns.tsx';
import { useApiAdminusersIdhistoryGetCollection } from '@r/api/generated/client.ts';
import type { UserHistoryUserHistoryReadModel } from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type UserHistory = UserHistoryUserHistoryReadModel;

type UserHistoryTableProps = {
  userId: string;
  registeredAt?: string | null;
};

export function UserHistoryTable({ userId, registeredAt }: UserHistoryTableProps) {
  const { t } = useTranslation();
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const historyQuery = useApiAdminusersIdhistoryGetCollection(userId, {
    page: apiPagination.pageIndex + 1,
    perPage: apiPagination.pageSize
  });
  useOrvalQueryErrorToast(historyQuery, t('toast.error.label'));

  const data = useMemo<UserHistory[]>(() => {
    const history: UserHistory[] = [...((historyQuery.data?.data as UserHistory[]) ?? [])];

    if (registeredAt && typeof registeredAt === 'string') {
      history.push({
        property: 'registeredAt',
        newValue: format(new Date(registeredAt), 'dd/MM/yyyy'),
        createdAt: registeredAt
      } as UserHistory);
    }

    return history;
  }, [historyQuery.data, registeredAt]);

  const { table } = useDataTable<UserHistory, unknown>({
    data,
    columns: useHistoryColumns<UserHistory>(),
    pageCount: historyQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: false,
    manualPaginate: true,
    manualSort: false,
    manualFilter: false
  });

  useEffect(() => {
    const { pageIndex, pageSize } = table.getState().pagination;
    if (pageIndex !== apiPagination.pageIndex || pageSize !== apiPagination.pageSize) {
      setApiPagination({ pageIndex, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().pagination.pageIndex, table.getState().pagination.pageSize]);

  if (historyQuery.isLoading || historyQuery.isFetching) {
    return (
      <DataTableSkeleton
        columnCount={5}
        cellWidths={['10rem', '10rem', '10rem', '10rem', '8rem']}
      />
    );
  }

  if (data.length === 0) return null;

  return <DataTable table={table} totalItems={historyQuery.data?.pagination?.totalItems} />;
}
